import {utils} from '@mc/redux';
import PropTypes from 'prop-types';
import React, {RefObject} from 'react';
import ReactDOM from 'react-dom';
import {connect} from 'react-redux';
import {Dispatch} from 'redux';
import {importReactNativeWebView, isReactNative} from './utils';
import FrameActions from './FrameActions';
import {FrameState} from './FrameReducer';

/**
 * Reference na WebView komponentu v případě, že jsme v React Native.
 *
 * @ignore
 */
let WebView: any;

if (isReactNative) {
    importReactNativeWebView()
        .then(RNWV => {
            WebView = RNWV.WebView;
        })
        .catch(error => {
            try {
                console.log('Failed to import WebView from react-native-webview', error);
            } catch (e) {
                // Ignorujeme když už nejde console.log nic nám nepomůže
            }
        });
}

/**
 * Příznak zda je nutné zprávy přenášet jako string.
 *
 * @ignore
 */
const isStringMessages = isReactNative || navigator.userAgent.indexOf('MSIE 9.0') >= 0;

/**
 * Properties frame.
 */
export interface FrameProps {
    /**
     * Oblast store, kde se nachází reducer komponenty.
     */
    area: string;

    /**
     * URL webové stránky pro zobrazení ve frame.
     */
    src: string;

    /**
     * Titulek frame pro přístupnost.
     * Použito pouze v prostředí webového prohlížeče.
     */
    title?: string;

    /**
     * Zda má webová stránka uvnitř frame povolenu geolokaci.
     */
    geolocationEnabled?: boolean;

    /**
     * Callback pro přijetí zprávy od webové stránky uvnitř frame.
     *
     * @param message - Zpráva.
     */
    onMessage?: (message: any) => void;
}

/**
 * Properties frame získané mapováním dispatch.
 *
 * @ignore
 */
export type DispatchProps = ReturnType<typeof mapDispatchToProps>;

/**
 * Properties frame získané mapováním stavu store.
 *
 * @ignore
 */
export type StateProps = ReturnType<typeof mapStateToProps>;

/**
 * Kompletní properties frame.
 *
 * @ignore
 */
export type Props = StateProps & DispatchProps & FrameProps;

/**
 * Interní implementace frame nenapojená na store.
 *
 * @ignore
 */
class UnconnectedFrame extends React.PureComponent<Props> {
    /**
     * Reference na interní low-level frame komponentu.
     */
    private frame?: RefObject<HTMLIFrameElement>;

    /**
     * Konstuktor.
     *
     * @param props - Výchozí properties.
     */
    constructor(props) {
        super(props);
        if (React.version.indexOf('16') === 0) {
            this.frame = React.createRef();
        }
    }

    setFrame = frame => {
        this.frame = frame;
    };

    /**
     * Pošle zprávu webové stránce uvnitř frame.
     *
     * @param data - Data zprávy.
     */
    private postMessage(data: any): void {
        const serializedData = isStringMessages ? JSON.stringify(data) : data;
        if (React.version.indexOf('16') === 0 || isReactNative) {
            if (this.frame) {
                if (isReactNative) {
                    (this.frame as any).postMessage(serializedData, '*');
                } else if (this.frame.current) {
                    this.frame.current.contentWindow!.postMessage(serializedData, '*');
                } else {
                    throw new Error('Frame found, but unable to send message');
                }
            } else {
                throw new Error('Frame not found');
            }
        } else {
            const frame: HTMLIFrameElement | null = ReactDOM.findDOMNode(this.refs.frame) as HTMLIFrameElement;
            if (frame) {
                frame.contentWindow!.postMessage(serializedData, '*');
            } else {
                throw new Error('Frame not found');
            }
        }
    }

    /**
     * Zpracuje zprávu od webové stránky ve frame.
     *
     * @param event - Událost se zprávou.
     */
    private handleMessage = (event: MessageEvent): void => {
        const {src, onMessage} = this.props;
        try {
            // V prohlížeči musíme kontrolovat origin, protože zráva mohla přijít i od jiného window.
            if (onMessage && (isReactNative || (src && src.indexOf(event.origin) === 0))) {
                if (isReactNative) {
                    event = (event as any).nativeEvent; // React native má nativní event obalen
                }
                try {
                    const message = isStringMessages ? JSON.parse(event.data) : event.data;
                    try {
                        onMessage(message);
                    } catch (err) {
                        // TODO @compel Vyřešit, aby se někam logovalo a nepadalo to
                        //console.error("[Frame] Error calling onMessage function", err, "\nInput data", data);
                    }
                } catch (err) {
                    // TODO @compel Vyřešit, aby se někam logovalo a nepadalo to
                    //console.error("[Frame] Error in parsing json or calling onMessage function", err, "\nOriginal event", event);
                }
            }
        } catch (err) {
            // TODO @compel Vyřešit, aby se někam logovalo a nepadalo to
            //console.error("[Frame] Error process handleMessage function", err, "\nOriginal event", event);
        }
    };

    /**
     * Zpracuje zprávy na odeslání ve frontě, pokud je potřeba.
     */
    private processMessagesIfNeeded(): void {
        const {postSuspended, messages, onPostDone} = this.props;
        if (!postSuspended && messages && messages.length > 0) {
            messages.forEach(this.postMessage, this);
            onPostDone();
        }
    }

    /**
     * Voláno po připojení komponenty.
     */
    componentDidMount(): void {
        if (!isReactNative) {
            if (this.props.geolocationEnabled && React.version.indexOf('16') !== 0) {
                // React 15 nezná allow atribut pro iframe a nepovolí ho, nastavujeme v componentDidMount ručně.
                const frame: HTMLIFrameElement | null = ReactDOM.findDOMNode(this.refs.frame) as HTMLIFrameElement;
                if (frame) {
                    frame.setAttribute('allow', this.getAllow()!);
                }
            }
            window.addEventListener('message', this.handleMessage);
        }
        this.processMessagesIfNeeded();
    }

    /**
     * Voláno po aktualizaci komponenty.
     */
    componentDidUpdate(): void {
        this.processMessagesIfNeeded();
    }

    /**
     * Voláno před odpojením komponenty.
     */
    componentWillUnmount(): void {
        if (!isReactNative) {
            window.removeEventListener('message', this.handleMessage);
        }
    }

    getAllow() {
        if (this.props.geolocationEnabled) {
            let allow = '';
            try {
                const url = new URL(this.props.src);
                allow = 'geolocation ' + url.origin;
            } catch (e) {
                allow = 'geolocation';
            }

            return allow;
        } else {
            return undefined;
        }
    }

    /**
     * Vykreslí komponentu.
     *
     * @returns Element.
     */
    render(): JSX.Element {
        const {
            area,
            src,
            title,
            geolocationEnabled,
            postSuspended,
            messages,
            onMessage,
            onPostDone,
            ...otherProps
        } = this.props;

        if (isReactNative) {
            return (
                <WebView
                    ref={this.setFrame}
                    source={{uri: src}}
                    geolocationEnabled={geolocationEnabled}
                    onMessage={this.handleMessage}
                    {...otherProps}
                />
            );
        }

        if (React.version.indexOf('16') === 0) {
            return <iframe ref={this.frame} src={src} title={title} allow={this.getAllow()} {...otherProps} />;
        }

        // React 15 nezná allow atribut pro iframe a nepovolí ho, nastavujeme v componentDidMount ručně.
        return <iframe ref="frame" title={title} src={src} {...otherProps} />;
    }
}

/**
 * Mapuje stav do properties.
 *
 * @param state - Stav store.
 * @param ownProps - Vlastní properties.
 * @returns Properies.
 * @ignore
 */
function mapStateToProps(state: any, ownProps: FrameProps) {
    const frame = utils.storeFromArea(state, ownProps.area) as FrameState;
    return {
        postSuspended: frame.postSuspended,
        messages: frame.messages,
    };
}

/**
 * Mapuje dispatch do properties.
 *
 * @param dispatch - Dispatch funkce
 * @param ownProps - Vlastní properties.
 * @returns Properties.
 * @ignore
 */
function mapDispatchToProps(dispatch: Dispatch, ownProps: FrameProps) {
    return {
        onPostDone: () => dispatch(FrameActions.clearMessages(ownProps.area)),
    };
}

/**
 * Interní implementace frame napojená na store.
 *
 * @ignore
 */
const ConnectedFrame = connect<StateProps, DispatchProps, FrameProps>(
    mapStateToProps,
    mapDispatchToProps,
)(UnconnectedFrame);

/**
 * Frame pro zobrazení webové stránky.
 *
 * - V prohlížeči představuje iframe.
 * - V React Native představuje WebView.
 *
 * @param props - Properties komponenty.
 * @returns Element.
 */
export default function Frame(props: FrameProps): JSX.Element {
    return <ConnectedFrame {...props} />;
}

/**
 * Typy properties.
 */
Frame.propTypes = {
    src: PropTypes.string.isRequired,
    area: PropTypes.string.isRequired,
    geolocationEnabled: PropTypes.bool,
    onMessage: PropTypes.func,
};

/**
 * Výchozí hodnoty properties.
 */
Frame.defaultProps = {
    geolocationEnabled: false,
};
